/**
 * Integrytas Group - Common Security Module (CSM)
 * Omnibox — Cmd/Ctrl+K command palette.
 *
 * Three result groups:
 *   Navigation — pages + open tabs (instant, from registry)
 *   Actions    — registered quick triggers (instant, from registry)
 *   Data       — entity searches (debounced 200 ms DB query)
 *
 * Keyboard: ↑ ↓ to move, Enter to select, Esc to close.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { commandRegistry } from '../../lib/command_registry';
import type { Command, EntityResult } from '../../lib/command_registry';

interface OmniboxProps {
  onClose: () => void;
}

type ResultItem =
  | { kind: 'command'; data: Command }
  | { kind: 'entity';  data: EntityResult };

const SECTION_LABELS: Record<string, string> = {
  navigation: 'omnibox.section.navigation',
  action:     'omnibox.section.actions',
  data:       'omnibox.section.data',
};

const SCOPE_ICONS: Record<string, string> = {
  navigation: '↗',
  action:     '⚡',
  data:       '🔍',
};

export function Omnibox({ onClose }: OmniboxProps) {
  const { t } = useTranslation();
  const inputRef  = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLDivElement>(null);
  const debounce  = useRef<ReturnType<typeof setTimeout>>();

  const [query,      setQuery]      = useState('');
  const [navItems,   setNavItems]   = useState<Command[]>([]);
  const [actionItems,setActionItems]= useState<Command[]>([]);
  const [dataItems,  setDataItems]  = useState<EntityResult[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [cursor,     setCursor]     = useState(0);

  // Build flat list for keyboard navigation
  const allItems = useMemo((): ResultItem[] => [
    ...navItems.map(d => ({ kind: 'command' as const, data: d })),
    ...actionItems.map(d => ({ kind: 'command' as const, data: d })),
    ...dataItems.map(d => ({ kind: 'entity' as const,  data: d })),
  ], [navItems, actionItems, dataItems]);

  // Focus input on open
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Recompute results whenever query changes
  useEffect(() => {
    setNavItems(commandRegistry.queryNavigation(query));
    setActionItems(commandRegistry.queryActions(query));

    clearTimeout(debounce.current);
    if (query.length < 2) { setDataItems([]); return; }

    setLoading(true);
    debounce.current = setTimeout(async () => {
      const results = await commandRegistry.queryEntities(query);
      setDataItems(results);
      setLoading(false);
    }, 200);

    return () => clearTimeout(debounce.current);
  }, [query]);

  // Reset cursor when results change
  useEffect(() => { setCursor(0); }, [allItems.length]);

  const select = useCallback((item: ResultItem) => {
    if (item.kind === 'command') item.data.onSelect();
    else item.data.onSelect();
    onClose();
  }, [onClose]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape')    { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, allItems.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && allItems[cursor]) { e.preventDefault(); select(allItems[cursor]); }
  };

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${cursor}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const renderItem = (item: ResultItem, idx: number) => {
    const isActive = idx === cursor;
    const label    = item.kind === 'command' ? item.data.label : item.data.label;
    const desc     = item.kind === 'command' ? item.data.description : item.data.description;
    const icon     = item.kind === 'command'
      ? SCOPE_ICONS[item.data.scope]
      : '→';

    return (
      <button
        key={`${item.kind}:${item.kind === 'command' ? item.data.id : item.data.id}`}
        data-idx={idx}
        type="button"
        onMouseEnter={() => setCursor(idx)}
        onClick={() => select(item)}
        className={[
          'flex w-full items-center gap-3 px-3 py-2 text-left text-csm-sm',
          isActive ? 'bg-primary text-white' : 'text-csm-ink hover:bg-csm-surface-alt',
        ].join(' ')}
      >
        <span className="w-5 shrink-0 text-center text-csm-xs opacity-60">{icon}</span>
        <span className="flex-1 truncate font-medium">{label}</span>
        {desc && (
          <span className={`truncate text-csm-xs ${isActive ? 'text-white/70' : 'text-csm-muted'}`}>
            {desc}
          </span>
        )}
      </button>
    );
  };

  const sections: { label: string; items: ResultItem[] }[] = [
    { label: 'navigation', items: navItems.map(d => ({ kind: 'command' as const, data: d })) },
    { label: 'action',     items: actionItems.map(d => ({ kind: 'command' as const, data: d })) },
    { label: 'data',       items: dataItems.map(d => ({ kind: 'entity' as const, data: d })) },
  ].filter(s => s.items.length > 0);

  let globalIdx = 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-xl rounded-lg border border-csm-border bg-white shadow-2xl overflow-hidden">
        {/* Search input */}
        <div className="flex items-center border-b border-csm-border px-3">
          <span className="mr-2 text-csm-muted text-csm-md">⌕</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('omnibox.placeholder')}
            className="flex-1 h-11 bg-transparent text-csm-md text-csm-ink placeholder-csm-muted
                       focus:outline-none"
          />
          {loading && (
            <span className="text-csm-xs text-csm-muted animate-pulse">
              {t('common.loading')}
            </span>
          )}
          <kbd className="ml-2 rounded border border-csm-border px-1.5 py-0.5 text-csm-xs text-csm-muted">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-80 overflow-y-auto">
          {sections.length === 0 && query && !loading && (
            <div className="px-4 py-6 text-center text-csm-sm italic text-csm-muted">
              {t('omnibox.no_results')}
            </div>
          )}

          {sections.length === 0 && !query && (
            <div className="px-4 py-4 text-csm-xs text-csm-muted">
              {t('omnibox.hint')}
            </div>
          )}

          {sections.map(section => (
            <div key={section.label}>
              <div className="border-b border-csm-border px-3 py-1 text-csm-2xs font-semibold
                              uppercase tracking-wider text-csm-muted bg-csm-surface-alt">
                {t(SECTION_LABELS[section.label] ?? section.label)}
              </div>
              {section.items.map(item => {
                const el = renderItem(item, globalIdx);
                globalIdx++;
                return el;
              })}
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="border-t border-csm-border bg-csm-surface-alt px-3 py-1.5
                        flex gap-4 text-csm-xs text-csm-muted">
          <span><kbd className="font-mono">↑↓</kbd> {t('omnibox.hint_navigate')}</span>
          <span><kbd className="font-mono">↵</kbd> {t('omnibox.hint_select')}</span>
          <span><kbd className="font-mono">Esc</kbd> {t('omnibox.hint_close')}</span>
        </div>
      </div>
    </div>
  );
}
